<template>
  <CheckoutStep>
    <template #header>
      <Stepper :has-bouygues-steps="hasSelectedMobilePlan(items)" />
    </template>
    <template #aside>
      <CheckoutSummary
        :discount
        :discount-token
        :items="availableItems"
        :summary
        :swapOffer
        with-extended-legal
      />
    </template>

    <ShippingAddress
      :items
      :loading
      :shippable-countries
      :shipping-address="addressList.deliver"
      :swap-offer
      with-same-billing-checkbox
      @update="handleUpdate"
    />
  </CheckoutStep>
</template>

<script setup lang="ts">
import { navigateTo } from '#imports'
import { computed, onMounted } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'

import CheckoutStep from '~/scopes/checkout/components/CheckoutStep/CheckoutStep.vue'
import CheckoutSummary from '~/scopes/checkout/components/CheckoutSummary/CheckoutSummary.vue'
import ShippingAddress from '~/scopes/checkout/components/ShippingAddress/ShippingAddress.vue'
import { useRedirectIfCartEmpty } from '~/scopes/checkout/composables/redirects/useRedirectIfCartEmpty'
import { useCheckoutTracking } from '~/scopes/checkout/composables/useCheckoutTracking'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import type { Discount } from '~/scopes/checkout/types/discount'
import { getAvailableItems } from '~/scopes/checkout/utils/cart'
import { hasSelectedMobilePlan } from '~/scopes/checkout/utils/hasSelectedMobilePlan'

import Stepper from '../../components/Stepper/Stepper.vue'

const props = defineProps<{
  loading: boolean
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  summary: ApiSpecs['/bm/cart']['GET']['response']['summary']
  swapOffer: BuybackOffer | null
  discount: Discount
  discountToken: string
}>()

const emit = defineEmits<{
  (e: 'update', callback: () => void): void
}>()

const { trackFunnel } = useCheckoutTracking()
const { redirectIfCartEmpty } = useRedirectIfCartEmpty('SHIPPING_ADDRESS')
const experiments = useExperiments()

const availableItems = computed(() => getAvailableItems(props.items))

const isSinglePageAddressesEnabled = computed(() => {
  return experiments['experiment.checkoutOnePageAddress'] === 'onePageAddress'
})

onMounted(() => {
  if (isSinglePageAddressesEnabled.value) {
    navigateTo({ name: CHECKOUT.ADDRESSES })

    return
  }

  redirectIfCartEmpty(props.items, CHECKOUT.CART)
  trackFunnel(props.items, props.swapOffer)
})

async function handleUpdate(sameAsShipping: boolean) {
  emit('update', async () => {
    await navigateTo({
      name: sameAsShipping
        ? CHECKOUT.ADDRESS_CONFIRMATION
        : CHECKOUT.BILLING_ADDRESS,
    })
  })
}
</script>
